import React, { forwardRef } from 'react';
import { View, Text, StyleSheet, Image, Dimensions } from 'react-native';
import { Svg, Circle, Defs, RadialGradient, Stop, Ellipse, Mask, G } from 'react-native-svg';
import { useLanguage } from '@/contexts/LanguageContext';
import { format } from 'date-fns';
import { ptBR, enUS, es } from 'date-fns/locale';
import { PADRINHO_LOGO_WHITE, SOBRIETY_STAR } from '@/constants/sobrietyCardImages';

interface ShareableSobrietyCardProps {
  days: number;
}

const SCREEN_WIDTH = Dimensions.get('window').width;
const CARD_WIDTH = SCREEN_WIDTH - 48;
const CARD_HEIGHT = 390;

const ShareableSobrietyCard = forwardRef<View, ShareableSobrietyCardProps>(({ days }, ref) => {
  const { t, language } = useLanguage();

  const getLocale = () => {
    switch (language) {
      case 'pt': return ptBR;
      case 'es': return es;
      default: return enUS;
    }
  };

  const formatCurrentDate = () => {
    const now = new Date();
    const locale = getLocale();
    
    if (language === 'pt') {
      return format(now, "d 'de' MMMM 'de' yyyy", { locale });
    } else if (language === 'es') {
      return format(now, "d 'de' MMMM 'de' yyyy", { locale });
    } else {
      return format(now, "MMMM d, yyyy", { locale });
    }
  };

  return (
    <View ref={ref} style={styles.card} collapsable={false}>
      <View style={styles.blueWaveContainer}>
        <Svg width="1040" height="1040" viewBox="0 0 1040 1040" fill="none" style={styles.blueWave}>
          <Defs>
            <RadialGradient id="whiteBlurCard" cx="50%" cy="50%" rx="50%" ry="50%">
              <Stop offset="0%" stopColor="white" stopOpacity={0.4} />
              <Stop offset="100%" stopColor="white" stopOpacity={0} />
            </RadialGradient>
            <RadialGradient id="blueBlurCard1" cx="50%" cy="50%" rx="50%" ry="50%">
              <Stop offset="0%" stopColor="#015480" stopOpacity={0.7} />
              <Stop offset="100%" stopColor="#015480" stopOpacity={0} />
            </RadialGradient>
            <RadialGradient id="blueBlurCard2" cx="50%" cy="50%" rx="50%" ry="50%">
              <Stop offset="0%" stopColor="#015480" stopOpacity={0.5} />
              <Stop offset="100%" stopColor="#015480" stopOpacity={0} />
            </RadialGradient>
            <Mask id="circleMask" maskUnits="userSpaceOnUse" x="0" y="0" width="1040" height="1040">
              <Circle cx="520" cy="520" r="520" fill="white" />
            </Mask>
          </Defs>
          <Circle cx="520" cy="520" r="520" fill="#669AB7" />
          <G mask="url(#circleMask)">
            <Ellipse cx="400" cy="100" rx="120" ry="100" fill="url(#whiteBlurCard)" />
            <Ellipse cx="300" cy="80" rx="100" ry="80" fill="url(#blueBlurCard1)" />
            <Ellipse cx="700" cy="60" rx="180" ry="120" fill="url(#blueBlurCard2)" />
          </G>
        </Svg>
      </View>
      
      <View style={styles.logoInWave}>
        <Image 
          source={{ uri: PADRINHO_LOGO_WHITE }} 
          style={styles.logoImage}
          resizeMode="contain"
        />
      </View>

      <View style={styles.badgeContainer}>
        <Image 
          source={{ uri: SOBRIETY_STAR }} 
          style={styles.badgeImage}
          resizeMode="contain"
        />
      </View>

      <View style={styles.textContainer}>
        <Text style={styles.mainText}>
          {t('sobriety.shareCard.line1')}{'\n'}
          <Text style={styles.daysText}>{days} {t('sobriety.shareCard.days')}</Text>
          {' '}{t('sobriety.shareCard.line2')}
        </Text>
        <Text style={styles.dateText}>{formatCurrentDate()}</Text>
      </View>
    </View>
  );
});

ShareableSobrietyCard.displayName = 'ShareableSobrietyCard';

const styles = StyleSheet.create({
  card: {
    width: CARD_WIDTH,
    height: CARD_HEIGHT,
    backgroundColor: '#F9F8F3',
    borderRadius: 14,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    overflow: 'hidden',
    alignItems: 'center',
    position: 'relative',
  },
  blueWaveContainer: {
    position: 'absolute',
    bottom: -955,
    left: -353,
    width: 1040,
    height: 1040,
  },
  blueWave: {
    position: 'absolute',
  },
  logoInWave: {
    position: 'absolute',
    bottom: 20,
    left: 0,
    right: 0,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 20,
  },
  logoImage: {
    width: 120,
    height: 24,
  },
  badgeContainer: {
    marginTop: 42,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
  },
  badgeImage: {
    width: 133,
    height: 133,
  },
  textContainer: {
    alignItems: 'center',
    gap: 4,
    marginTop: 16,
    paddingHorizontal: 20,
    zIndex: 10,
  },
  mainText: {
    fontFamily: 'Sentient-Regular',
    fontSize: 20,
    fontStyle: 'italic',
    fontWeight: '400',
    lineHeight: 24,
    color: '#151515',
    textAlign: 'center',
  },
  daysText: {
    fontFamily: 'Sentient-Regular',
    fontSize: 20,
    fontStyle: 'italic',
    fontWeight: '400',
    color: '#151515',
  },
  dateText: {
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    fontWeight: '500',
    lineHeight: 17,
    color: '#7E7D75',
    textAlign: 'center',
    marginTop: 4,
  },
});

export default ShareableSobrietyCard;
